import { ChangeEvent, useCallback, useEffect, useState } from 'react';
import { Text, Button, Flex, Skeleton, Switch, useToast } from '@chakra-ui/react';
import { GetParamsObject } from '@/model/query-params';
import { useFiltersForm } from '@/utils/useFiltersForm';
import { PageContent } from '@/components/PageContent';
import { SectionBox } from '@/components/SectionBox';
import { ChakraFeatherIcon } from '@/components/ChakraFeatherIcon';
import { breadcrumb } from './breadcrumb';
import { PostsFiltersBox } from './components/PostsFiltersBox';
import { PostsTable } from './components/PostsTable';
import { PostsFilters } from './types';
import { Posts } from '@/model';
import { ConfirmationModal } from '@/components/ConfirmationModal';
import { useAppApplicationComponentArea } from '@/services/queries/admin-config';
import { ApplicationComponentId } from '@/model/application-component';
import { useEnableApplicationComponentMutation } from '@/services/queries/admin-config';
import CenteredSpinner from '@/components/CenterSpinner/CenteredSpinner';
import { useDeletePostsMutation, useGetPosts, useGetPostsById } from '@/services/queries/posts';
import { PostsCreateModal } from './components/PostsCreateModal';
import { DateFilterInputValue } from '@/components/DateRangeFilterFields/types';
import { PostsEditModal } from './components/PostsEditModal';
import { useTranslation } from 'react-i18next';
import { useAllClub } from '@/services/queries/club';

export const InteractiveAreaPosts = () => {
  const { t } = useTranslation();
  const toast = useToast();

  const [isAppAreaActive, setIsAppAreaActive] = useState(false);

  const { data: appAreaData, isLoading: isLoadingAppArea } = useAppApplicationComponentArea(ApplicationComponentId.POSTS);
  const { mutate: enableAppAreaMutation, isLoading: isLoadingEnableOrDisableAppArea } = useEnableApplicationComponentMutation();
  const [pendingAppAreaState, setPendingAppAreaState] = useState<boolean | null>(null);
  const [isDialogAppAreaState, setIsDialogAppAreaState] = useState(false);

  const cancelAppAreaChange = () => {
    setIsDialogAppAreaState(false);
    setPendingAppAreaState(null);
  };

  const handleAppAreaChange = (event: ChangeEvent<HTMLInputElement>) => {
    const isChecked = event.target.checked;
    setPendingAppAreaState(isChecked);
    setIsDialogAppAreaState(true);
  };

  const confirmAppAreaChange = () => {
    if (pendingAppAreaState === null) return;

    const successActionString = pendingAppAreaState
      ? t('pages.application_posts.post_enable_success')
      : t('pages.application_posts.post_disable_success');
    const errorActionString = pendingAppAreaState
      ? t('pages.application_posts.post_enable_error')
      : t('pages.application_posts.post_disable_error');

    setIsAppAreaActive(pendingAppAreaState);

    enableAppAreaMutation([{ component_id: ApplicationComponentId.POLLS, active: pendingAppAreaState }], {
      onSuccess: () => {
        toast({
          status: 'success',
          title: successActionString,
        });
      },
      onError: () => {
        toast({
          status: 'error',
          title: errorActionString,
        });

        setIsAppAreaActive(!pendingAppAreaState);
      },
    });

    setIsDialogAppAreaState(false);
    setPendingAppAreaState(null);
  };

  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  const [selectedItem, setSelectedItem] = useState<Posts | undefined>(undefined);

  const deleteMutation = useDeletePostsMutation();
  const getItemByUUID = useGetPostsById();

  const filtersForm = useFiltersForm<PostsFilters>({
    defaultValues: {
      team_uuid: '',
      content: '',
      status: '',
      dateValue: DateFilterInputValue.ALL,
      start_date: '',
      end_date: '',
    },
  });

  const filterData = filtersForm.watch();

  const [sorting, setSorting] = useState<{ field: string; desc: boolean }>({ field: 'created_at', desc: true });
  const [pagination, setPagination] = useState<{ pageNumber: number; pageSize: number }>({
    pageNumber: 1,
    pageSize: 10,
  });

  const params = GetParamsObject(
    filterData,
    pagination.pageNumber,
    pagination.pageSize,
    sorting.desc ? 'desc' : 'asc',
    sorting.field,
  );

  const { data: itemData, isLoading, forceRefetch } = useGetPosts(params);
  const { data: clubs = [] } = useAllClub(true);

  const itemsData = itemData?.data || [];

  const onHandleDelete = (item: Posts) => {
    setSelectedItem(item);
    setIsConfirmModalOpen(true);
  };

  const onHandleDeleteConfirm = () => {
    if (!selectedItem?.uuid) return;

    setIsConfirmModalOpen(false);

    deleteMutation.mutate(selectedItem.uuid, {
      onSuccess: () => {
        toast({
          title: t('pages.application_posts.post_delete_success'),
          status: 'success',
        });
        forceRefetch();
        setSelectedItem(undefined);
      },
      onError: (e: any) => {
        setSelectedItem(undefined);
        toast({
          status: 'error',
          title: e?.message || t('pages.application_posts.post_delete_error'),
        });
      },
    });
  };

  const onHandleEdit = (item: Posts) => {
    setSelectedItem(item);
    setIsEditModalOpen(true);
  };

  const onHandleEditDone = useCallback(() => {
    setIsEditModalOpen(false);
    setSelectedItem(undefined);
    forceRefetch();
  }, [forceRefetch]);

  const onHandleCreateDone = useCallback(() => {
    setIsCreateModalOpen(false);
    forceRefetch();
  }, [forceRefetch, setIsCreateModalOpen]);

  useEffect(() => {
    if (!appAreaData) return;

    setIsAppAreaActive(appAreaData.is_active);
  }, [appAreaData]);

  return (
    <PageContent
      title={t('pages.application_posts.post_title')}
      breadcrumbItems={breadcrumb}
      actionsContent={
        <Button
          onClick={() => setIsCreateModalOpen(true)}
          display='flex'
          alignItems='center'
          gap={2}
          width={{ base: '100%', md: 'initial' }}
        >
          <ChakraFeatherIcon name='plus-circle' />
          {t('pages.application_posts.post_button_create')}
        </Button>
      }
    >
      <SectionBox bodyProps={{ gap: 'small' }}>
        <Flex direction={{ base: 'column', md: 'row' }} justifyContent={{ md: 'space-between' }} gap={4}>
          <Flex alignItems='center' gap={4}>
            {isLoadingAppArea ? (
              <>
                <Skeleton w='130px' h='21px' />
                <Skeleton w='102px' h='21px' />
              </>
            ) : (
              <>
                <Text fontWeight='bold' color='brand.500'>
                  {t('pages.application_posts.post_posts_label')}
                </Text>
                <Switch size='sm' isChecked={isAppAreaActive} onChange={handleAppAreaChange}>
                  {isAppAreaActive
                    ? t('pages.application_posts.post_enabled_label')
                    : t('pages.application_posts.post_disabled_label')}
                </Switch>
              </>
            )}
          </Flex>
        </Flex>
        <SectionBox bodyProps={{ gap: 'small' }}>
          <PostsFiltersBox clubs={clubs} filtersForm={filtersForm} forceRefetch={forceRefetch} />
        </SectionBox>

        <Flex direction='column' gap='deka' mt={8}>
          <PostsTable
            data={itemsData}
            isLoading={isLoading}
            sorting={sorting}
            pageNumber={pagination.pageNumber}
            pageSize={pagination.pageSize}
            totalPages={itemData?.meta.pages || 0}
            totalItems={itemData?.meta.total || 0}
            onSortingChange={(field, desc) => setSorting({ field, desc })}
            onPaginationChange={(pageNumber, pageSize) => setPagination({ pageNumber, pageSize })}
            onEditClick={item => onHandleEdit(item)}
            onDeleteClick={item => onHandleDelete(item)}
          />
        </Flex>
      </SectionBox>

      {selectedItem && (
        <PostsEditModal
          isOpen={!!isEditModalOpen}
          onClose={() => {
            setIsEditModalOpen(false);
            setSelectedItem(undefined);
          }}
          item={selectedItem}
          onDone={onHandleEditDone}
        />
      )}

      <PostsCreateModal
        isOpen={!!isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onDone={onHandleCreateDone}
      />

      <ConfirmationModal
        isOpen={isConfirmModalOpen}
        onClose={() => {
          setIsConfirmModalOpen(false);
          setSelectedItem(undefined);
        }}
        onConfirm={onHandleDeleteConfirm}
        isLoading={false}
        title={t('pages.application_posts.post_delete_modal_title')}
        description={
          <Text>
            {t('pages.application_posts.post_delete_modal_description')}{' '}
            <Text as='span' fontWeight='bold'>
              "{selectedItem?.identifier}"
            </Text>
            ?
          </Text>
        }
      />

      <ConfirmationModal
        isOpen={isDialogAppAreaState}
        onClose={cancelAppAreaChange}
        onConfirm={confirmAppAreaChange}
        title={t('pages.application_posts.post_toggle_modal_title')}
        description={
          <Text>
            {pendingAppAreaState
              ? t('pages.application_posts.post_toggle_modal_description_enable')
              : t('pages.application_posts.post_toggle_modal_description_disable')}
          </Text>
        }
      />

      <CenteredSpinner isLoading={getItemByUUID.isLoading || isLoadingEnableOrDisableAppArea || deleteMutation.isLoading} />
    </PageContent>
  );
};
